<template>
  <article
    class="border rounded-xl p-4 flex gap-4 items-start relative"
    :class="!inStock ? 'opacity-50' : ''"
  >
    <NuxtLink :to="productLink" class="block w-24 shrink-0">
      <template v-if="item.image">
        <img
          :src="item.image"
          alt=""
          class="w-24 h-24 object-cover rounded-lg bg-gray-100"
        />
      </template>
      <div
        v-else
        class="w-24 h-24 rounded-lg bg-gray-100 grid place-items-center text-gray-400"
      >
        <ImageIcon :size="24" />
      </div>
    </NuxtLink>

    <div class="flex-1 min-w-0">
      <NuxtLink :to="productLink" class="font-medium line-clamp-2 pr-4">
        {{ item.title }}
      </NuxtLink>
      <div class="text-sm text-gray-500 mt-1 flex flex-wrap gap-3">
        <span v-if="item.color">Колір: {{ item.color }}</span>
        <span v-if="item.size">Розмір: {{ item.size }}</span>
        <span
          v-if="badgeText"
          class="inline-flex items-center rounded-full bg-amber-100 text-amber-900 px-2 py-0.5 text-xs"
        >
          {{ badgeText }}
        </span>
        <span>Ціна: {{ formatPrice(item.price) }}</span>
      </div>

      <div class="mt-3 flex items-center gap-3">
        <div class="inline-flex items-center border rounded-lg">
          <button class="px-2 py-1" @click="emit('dec')">−</button>
          <input
            class="w-10 text-center outline-none py-1"
            :value="item.qty"
            inputmode="numeric"
            @input="onInput"
          />
          <button class="px-2 py-1" @click="emit('inc')">+</button>
        </div>

        <div class="ml-auto font-semibold">
          {{ formatPrice(Number(item.price || 0) * Number(item.qty || 0)) }}
        </div>

        <button
          class="text-sm underline text-gray-600 hover:text-gray-900 absolute right-4 top-4"
          @click="emit('remove')"
        >
          <Trash2 :size="18" />
        </button>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { ImageIcon, Trash2 } from "lucide-vue-next";
import { formatPrice } from "~/utils/money";
import { cartItemBadgeText } from "~/composables/product/variantDisplay";

/* ====================================================================
 *  CartItemRow — рядок кошика. Презентаційний: без Firestore і без
 *  стора, наявність приходить згори (`inStock`), усі дії — через emit.
 * ==================================================================== */

interface CartItemLike {
  key?: string;
  productId?: string;
  variantId?: string;
  slug?: string;
  title?: string;
  price?: number;
  qty?: number;
  image?: string;
  color?: string;
  size?: string;
}

const props = withDefaults(
  defineProps<{ item: CartItemLike; inStock?: boolean }>(),
  { inStock: true },
);

const emit = defineEmits<{
  inc: [];
  dec: [];
  remove: [];
  "qty-input": [value: number];
}>();

const badgeText = computed(() => cartItemBadgeText(props.item));

/** Глибоке посилання на варіант: інакше різні варіанти ведуть на перший. */
const productLink = computed(() => {
  const it = props.item;
  const base = it.slug ? `/product/${it.slug}` : `/product/${it.productId}`;
  const qs: string[] = [];
  if (it.variantId) qs.push(`vid=${encodeURIComponent(String(it.variantId))}`);
  if (it.color) qs.push(`color=${encodeURIComponent(String(it.color))}`);
  if (it.size) qs.push(`size=${encodeURIComponent(String(it.size))}`);
  return qs.length ? `${base}?${qs.join("&")}` : base;
});

function onInput(e: Event) {
  const raw = String((e.target as HTMLInputElement)?.value || "").replace(
    /\D/g,
    "",
  );
  emit("qty-input", Number(raw || 0));
}
</script>

<style scoped>
input[inputmode="numeric"] {
  width: 2.5rem;
}
</style>
